import { useCallback, useEffect, useState } from "react"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { httpSharePointRepository } from "../../data/repositories/sharepoint-repository"
import type { SyncSelection, SyncStatus } from "../../domain/entities/sharepoint"

const repo = httpSharePointRepository

// Persist tracked sync ids + retry links per KB in sessionStorage so they
// survive leaving/re-entering the KB detail page (which unmounts this hook) and
// closing/reopening the dialog. Scoped to the tab, gone on tab close.
const idsKey = (kbId: string) => `sharepoint:sync:${kbId}`
const linksKey = (kbId: string) => `sharepoint:sync-links:${kbId}`

const readStoredIds = (kbId: string): string[] => {
  try {
    const raw = window.sessionStorage.getItem(idsKey(kbId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : []
  } catch {
    return []
  }
}

const readStoredLinks = (kbId: string): Record<string, string> => {
  try {
    const raw = window.sessionStorage.getItem(linksKey(kbId))
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

const write = (key: string, value: unknown, empty: boolean) => {
  try {
    if (empty) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* sessionStorage unavailable — state still works in-memory */
  }
}

type Attempt = { status: SyncStatus | null; errored: boolean }

export type SyncJob = {
  id: string
  status: SyncStatus | null // the original run
  errored: boolean
  retry: Attempt | null // latest retry attempt (tail of the chain), null if none
  retryTargetId: string // id whose retryable failures a new retry should resend
  retrying: boolean // a retry POST is in flight (before its progress appears)
}

const isTerminal = (a: Attempt): boolean =>
  a.errored ||
  (!!a.status && a.status.status !== "running" && a.status.status !== "preparing")

export const useSharePointSync = (kbId: string) => {
  const qc = useQueryClient()
  const [syncIds, setSyncIds] = useState<string[]>(() => readStoredIds(kbId))
  const [links, setLinks] = useState<Record<string, string>>(() =>
    readStoredLinks(kbId),
  )
  const [retrying, setRetrying] = useState<Set<string>>(() => new Set())

  const addSync = useCallback(
    (id: string) => {
      setSyncIds((prev) => {
        if (prev.includes(id)) return prev
        const next = [...prev, id]
        write(idsKey(kbId), next, next.length === 0)
        return next
      })
    },
    [kbId],
  )

  const linkRetry = useCallback(
    (targetId: string, childId: string) => {
      setLinks((prev) => {
        const next = { ...prev, [targetId]: childId }
        write(linksKey(kbId), next, false)
        return next
      })
    },
    [kbId],
  )

  const start = useMutation({
    mutationFn: (params: { connectionId: string; items: SyncSelection[] }) =>
      repo.sync({ connectionId: params.connectionId, kbId, items: params.items }),
  })

  const startNew = useCallback(
    (params: { connectionId: string; items: SyncSelection[] }) => {
      start.mutate(params, { onSuccess: (id) => addSync(id) })
    },
    [start, addSync],
  )

  const retry = useCallback(
    (targetId: string, params: { connectionId: string; items: SyncSelection[] }) => {
      setRetrying((prev) => new Set(prev).add(targetId))
      const clear = () =>
        setRetrying((prev) => {
          const n = new Set(prev)
          n.delete(targetId)
          return n
        })
      start.mutate(params, {
        onSuccess: (childId) => {
          addSync(childId)
          linkRetry(targetId, childId)
          clear()
        },
        onError: clear,
      })
    },
    [start, addSync, linkRetry],
  )

  const dismiss = useCallback(
    (rootId: string) => {
      // Remove the whole retry chain (root + every child).
      const chain: string[] = []
      const seen = new Set<string>()
      let t: string | undefined = rootId
      while (t && !seen.has(t)) {
        chain.push(t)
        seen.add(t)
        t = links[t]
      }
      setSyncIds((prev) => {
        const next = prev.filter((x) => !seen.has(x))
        write(idsKey(kbId), next, next.length === 0)
        return next
      })
      setLinks((prev) => {
        const next = { ...prev }
        chain.forEach((c) => delete next[c])
        write(linksKey(kbId), next, Object.keys(next).length === 0)
        return next
      })
    },
    [kbId, links],
  )

  const results = useQueries({
    queries: syncIds.map((id) => ({
      queryKey: ["sharepoint-sync", id],
      queryFn: () => repo.pollSync(id),
      enabled: !!id,
      retry: 2,
      refetchInterval: (query: { state: { status: string; data?: SyncStatus } }) => {
        // A persisted sync may have expired on the backend (Redis TTL 1h): the
        // poll then errors — stop retrying and let the UI surface it.
        if (query.state.status === "error") return false
        const s = query.state.data
        return s && s.status !== "running" && s.status !== "preparing" ? false : 1500
      },
    })),
  })

  const attemptOf = (id: string): Attempt => {
    const i = syncIds.indexOf(id)
    const r = results[i]
    return { status: r?.data ?? null, errored: !!r?.isError }
  }

  const tailOf = (id: string): string => {
    let t = id
    const seen = new Set([id])
    while (links[t] && !seen.has(links[t])) {
      t = links[t]
      seen.add(t)
    }
    return t
  }

  const childIds = new Set(Object.values(links))
  const jobs: SyncJob[] = syncIds
    .filter((id) => !childIds.has(id)) // only chain roots become rows
    .map((id) => {
      const self = attemptOf(id)
      const tail = tailOf(id)
      const hasRetry = tail !== id
      return {
        id,
        status: self.status,
        errored: self.errored,
        retry: hasRetry ? attemptOf(tail) : null,
        retryTargetId: tail,
        retrying: retrying.has(tail),
      }
    })

  // Refresh the document list whenever the set of finished attempts changes.
  const finishedKey = syncIds
    .filter((id) => isTerminal(attemptOf(id)))
    .join(",")
  useEffect(() => {
    if (finishedKey) {
      qc.invalidateQueries({ queryKey: ["documents"] })
    }
  }, [finishedKey, qc])

  return { start: startNew, retry, starting: start.isPending, jobs, dismiss }
}
